/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';

// models and services
import { ClickableTileMood, ClickableTileViewModel } from 'src/types/view-models/clickable-tile.view.model';
import { UserUtils } from 'src/types/user.model';
import { DLSSize } from 'src/types/dls-size.enum';
import { ProgressTileViewModel } from 'src/types/view-models/progress-tile.view.model';
import { ReturningDialogViewModel } from 'src/types/view-models/returning-dialog.view.model';
import ClickableTile from 'src/bw/clickable-tile/ClickableTile';
import ProgressTile from 'src/bw/progress-tile/ProgressTile';
import LiteralsService from '../../services/literals/LiteralsService';
import NavigationService from '../../services/navigation.service';

// components
import { ButtonPrimary, IconClose } from '@americanexpress/dls-react';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import InsightsStatsBox from 'src/components/atomic/molecules/insightsStatsBox/InsightsStatsBox';
import NewTransactionsRecap from 'src/components/atomic/molecules/newTransactionsRecap/NewTransactionsRecap';

// icons and css
import { ReactComponent as BudgetIcon } from 'src/bw/bw-drawer/status-drawer/icons/budget.svg';
import styles from './ReturningDialog.module.scss';
import FormatService from '../../services/format.service';
import { LocalDate } from '@js-joda/core';

export default class ReturningDialog extends Component<ReturningDialogViewModel> {
    public override render(): ReactComponentElement<any> {
        if (!this.props.stats || !this.props.status) return null;

        const netWorthDifference = this.props.stats.netWorth.currentAmount - this.props.stats.netWorth.previousAmount;
        const savingsDifference =
            this.props.stats.savingsAccounts.currentAmount - this.props.stats.savingsAccounts.previousAmount;
        const investmentsDifference =
            this.props.stats.investmentAccounts.currentAmount - this.props.stats.investmentAccounts.previousAmount;

        return (
            <Dialog
                fullWidth={true}
                className={styles.returningDialog}
                open={this.props.open}
                maxWidth={DLSSize.MEDIUM}
            >
                <DialogContent>
                    <div className={styles.returningDialogInner}>
                        <div className={styles.firstRow}>
                            <div className="title">
                                <Typography variant={TypographyVariants.HEADING_S}>
                                    {this.props.stats.changesSinceDaysAgo === 7
                                        ? LiteralsService.getLiteral('ReturningDialog.headlineThisWeek')
                                        : LiteralsService.getLiteral('ReturningDialog.headlineSinceLogin')}
                                </Typography>
                                <Typography variant={TypographyVariants.HEADING_S}>
                                    {FormatService.formatDate(
                                        LocalDate.now().minusDays(this.props.stats.changesSinceDaysAgo)
                                    )}
                                    -{FormatService.formatDate(LocalDate.now())}
                                </Typography>
                            </div>
                            <div>
                                <ClickableIcon
                                    icon={<IconClose />}
                                    className="close"
                                    onClick={() => this.props.onClose()}
                                />
                            </div>
                        </div>
                        <div className={styles.secondRow}>
                            <div className={styles.column}>
                                <div>
                                    <Typography variant={TypographyVariants.BODY} className={styles.introText}>
                                        Your plan is
                                    </Typography>
                                </div>
                                <div>
                                    <Typography
                                        variant={TypographyVariants.HEADING_XL}
                                        className={this.props.status.replace('-', '')}
                                    >
                                        {UserUtils.getStatusMessage(this.props.status)}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        {this.getSavingsRow()}
                        {this.getBudgetRow()}
                        <div className={styles.fifthRow}>
                            <InsightsStatsBox
                                netWorthDifference={netWorthDifference}
                                netWorthPercentChange={this.props.stats.netWorth.percentChange}
                                netWorthGlobalPercentChange={this.props.stats.netWorth.globalPercentChange}
                                savingsDifference={savingsDifference}
                                savingsPercentChange={this.props.stats.savingsAccounts.percentChange}
                                savingsGlobalPercentChange={this.props.stats.savingsAccounts.globalPercentChange}
                                investmentsDifference={investmentsDifference}
                                investmentsPercentChange={this.props.stats.investmentAccounts.percentChange}
                                investmentsGlobalPercentChange={this.props.stats.investmentAccounts.globalPercentChange}
                            />
                        </div>
                        <div className={styles.sixthRow}>
                            <NewTransactionsRecap
                                topDeposit={this.props.stats.topDeposit}
                                topExpense={this.props.stats.topExpense}
                                newTransactions={this.props.stats.newTransactions}
                                handleOpenIncomeSummary={this.props.handleOpenIncomeSummary}
                                handleOpenExpenses={this.props.handleOpenExpenses}
                                handleOpenTransactions={this.props.handleOpenTransactions}
                                handleOpenTransactionWithId={this.props.handleOpenTransactionWithId}
                            />
                        </div>
                        <div className={styles.buttonContainer}>
                            <ButtonPrimary
                                onClick={() => {
                                    this.props.onClose();
                                    NavigationService.goTo(this.props.history, '/insights').catch(console.error);
                                }}
                            >
                                {LiteralsService.getLiteral('ReturningDialog.viewMoreText')}
                            </ButtonPrimary>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    private getBudgetRow(): JSX.Element {
        if (!_.get(this.props, 'stats.overBudget')) return null;
        return (
            <div className={styles.fourthRow}>
                <ClickableTile {...this.getClickableTileViewData()} />
            </div>
        );
    }

    private getSavingsRow(): JSX.Element {
        const savingsGoals = _.get(this.props, 'stats.savingsCards');
        if (savingsGoals && !savingsGoals.length) return null;
        if (!_.get(this.props, 'stats.savingsOverview')) return null;
        return (
            <div className={styles.thirdRow}>
                <ProgressTile {...this.getProgressTileViewData()} />
            </div>
        );
    }

    private getClickableTileViewData(): ClickableTileViewModel {
        return {
            action: () => {
                NavigationService.goTo(this.props.history, '/budgets').catch(console.error);
                this.props.onClose();
            },
            amount: this.props.stats.overBudget.toString(),
            emphasizedWord: 'overspending',
            height: '72px',
            history: this.props.history,
            leftIcon: <BudgetIcon />,
            mood: ClickableTileMood.BAD,
            rightText: 'View budgets',
            status: "You're overspending in $amount budgets",
            width: '100%'
        };
    }

    private getProgressTileViewData(): ProgressTileViewModel {
        return {
            headline: LiteralsService.getLiteral('ProgressTile.headline'),
            progress:
                this.props.stats.savingsOverview.currentPercent - this.props.stats.savingsOverview.previousPercent,
            progressBar: {
                animationDelay: 200,
                animationSpeed: '1s',
                current: this.props.stats.savingsOverview.currentPercent,
                max: 100,
                min: 0,
                width: '100%'
            },
            width: '100%'
        };
    }
}
